import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  History, 
  ArrowUpRight, 
  ArrowDownLeft, 
  Gift, 
  ExternalLink,
  Search,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useWallet } from '@/context/WalletContext';
import { formatNumber, formatDateTime } from '@/lib/utils';

const BLOCK_EXPLORER = 'https://sepolia.basescan.org';

// Transaction types
const TX_TYPES = {
  STAKE: 'stake',
  UNSTAKE: 'unstake',
  CLAIM: 'claim',
  TRANSFER_IN: 'transfer_in',
  TRANSFER_OUT: 'transfer_out'
};

const TX_TYPE_CONFIG = {
  [TX_TYPES.STAKE]: { 
    icon: ArrowUpRight, 
    label: 'Stake', 
    color: 'text-green-500',
    bgColor: 'bg-green-500/10'
  },
  [TX_TYPES.UNSTAKE]: { 
    icon: ArrowDownLeft, 
    label: 'Unstake', 
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10'
  },
  [TX_TYPES.CLAIM]: { 
    icon: Gift, 
    label: 'Claim Rewards', 
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10'
  },
  [TX_TYPES.TRANSFER_IN]: { 
    icon: ArrowDownLeft, 
    label: 'Received', 
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10'
  },
  [TX_TYPES.TRANSFER_OUT]: { 
    icon: ArrowUpRight, 
    label: 'Sent', 
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10'
  }
};

// Mock transaction data (in production, fetch from blockchain events)
const generateMockTransactions = () => {
  const types = Object.values(TX_TYPES);
  const statuses = ['confirmed', 'pending', 'failed'];
  const transactions = [];
  
  for (let i = 0; i < 15; i++) {
    const type = types[Math.floor(Math.random() * 3)]; // Only stake, unstake, claim for demo
    const daysAgo = Math.floor(Math.random() * 30);
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    
    transactions.push({
      id: `tx-${i}`,
      hash: `0x${Math.random().toString(16).slice(2, 10)}...${Math.random().toString(16).slice(2, 6)}`,
      fullHash: `0x${Array(64).fill(0).map(() => Math.floor(Math.random() * 16).toString(16)).join('')}`,
      type,
      amount: (Math.random() * 500 + 10).toFixed(2),
      status: i < 12 ? 'confirmed' : statuses[Math.floor(Math.random() * 3)],
      timestamp: date.toISOString(),
      blockNumber: 43000000 + Math.floor(Math.random() * 500000),
      gasUsed: (Math.random() * 100000 + 50000).toFixed(0)
    });
  }
  
  return transactions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

export default function StakingHistory() {
  const { walletAddress, isConnected } = useWallet();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    // Simulate loading transactions
    const loadTransactions = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setTransactions(generateMockTransactions());
      setLoading(false);
    };
    
    if (isConnected) {
      loadTransactions();
    } else {
      setLoading(false);
    }
  }, [isConnected]);

  const filteredTransactions = transactions.filter(tx => {
    if (filter !== 'all' && tx.type !== filter) return false;
    if (search && !tx.hash.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    totalStaked: transactions.filter(t => t.type === TX_TYPES.STAKE && t.status === 'confirmed')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0),
    totalUnstaked: transactions.filter(t => t.type === TX_TYPES.UNSTAKE && t.status === 'confirmed')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0),
    totalClaimed: transactions.filter(t => t.type === TX_TYPES.CLAIM && t.status === 'confirmed')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0),
    txCount: transactions.filter(t => t.status === 'confirmed').length
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-500/10 text-green-500"><CheckCircle className="w-3 h-3 mr-1" />Confirmed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/10 text-yellow-500"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/10 text-red-500"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return null;
    }
  };

  return (
    <DashboardLayout title="Staking History">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Stats Overview */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <div className="text-sm text-muted-foreground mb-1">Total Staked</div>
              <div className="text-2xl font-bold text-green-500">{formatNumber(stats.totalStaked)} KOJI</div>
            </CardContent>
          </Card>
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <div className="text-sm text-muted-foreground mb-1">Total Unstaked</div>
              <div className="text-2xl font-bold text-yellow-500">{formatNumber(stats.totalUnstaked)} KOJI</div>
            </CardContent>
          </Card>
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <div className="text-sm text-muted-foreground mb-1">Rewards Claimed</div>
              <div className="text-2xl font-bold text-purple-500">{formatNumber(stats.totalClaimed)} KOJI</div>
            </CardContent>
          </Card>
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <div className="text-sm text-muted-foreground mb-1">Total Transactions</div>
              <div className="text-2xl font-bold">{stats.txCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="glass border-white/10">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="search-tx-input"
                  placeholder="Search by transaction hash..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10"
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger data-testid="filter-select" className="w-[180px] bg-white/5 border-white/10">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Transactions</SelectItem>
                  <SelectItem value={TX_TYPES.STAKE}>Stakes</SelectItem>
                  <SelectItem value={TX_TYPES.UNSTAKE}>Unstakes</SelectItem>
                  <SelectItem value={TX_TYPES.CLAIM}>Rewards</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Transaction List */}
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Transaction History
            </CardTitle>
            <CardDescription>
              Your staking activity on the KOJIRU platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!isConnected ? (
              <div className="text-center py-12 text-muted-foreground">
                <History className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Connect your wallet to view transaction history</p>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <History className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>No transactions found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTransactions.map((tx, i) => {
                  const config = TX_TYPE_CONFIG[tx.type];
                  const Icon = config.icon;
                  
                  return (
                    <motion.div
                      key={tx.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl ${config.bgColor} flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${config.color}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{config.label}</span>
                            {getStatusBadge(tx.status)}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(tx.timestamp)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <div className={`font-bold ${tx.type === TX_TYPES.STAKE ? 'text-green-500' : tx.type === TX_TYPES.UNSTAKE ? 'text-yellow-500' : 'text-purple-500'}`}>
                            {tx.type === TX_TYPES.STAKE ? '+' : tx.type === TX_TYPES.UNSTAKE ? '-' : '+'}{tx.amount} KOJI
                          </div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {tx.hash}
                          </div>
                        </div>
                        <a
                          href={`${BLOCK_EXPLORER}/tx/${tx.fullHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4 text-muted-foreground" />
                        </a>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
